import { gql } from '@apollo/client';
import { ROUTE_FIELDS } from './queries';

export const MARK_STOP_COMPLETED = gql`
    mutation MarkStopCompleted($routeId: ID!, $stopId: ID!) {
        markStopCompleted(routeId: $routeId, stopId: $stopId) {
            id
            status
            track
            name
            actionLog {
                id
                action
                timestamp
                userId
                userEmail
                stop {
                    id
                    name
                }
            }
            stops {
                id
                name
                status
                stationId
                type
                asynchronousChildren
            }
        }
    }
`;

export const UNMARK_STOP_COMPLETED = gql`
    mutation UnmarkStopCompleted($routeId: ID!, $stopId: ID!) {
        unMarkStopCompleted(routeId: $routeId, stopId: $stopId) {
            id
            name
            status
            track
            actionLog {
                id
                action
                timestamp
                userId
                userEmail
                stop {
                    id
                    name
                }
            }
            stops {
                id
                name
                status
                stationId
                type
                asynchronousChildren
            }
        }
    }
`;

export const CANCEL_ROUTE = gql`
    mutation CancelRoute($id: ID!) {
        cancelRoute(id: $id) {
            ...RouteFields
        }
    }
    ${ROUTE_FIELDS}
`;

export const DELETE_ROUTE = gql`
    mutation DeleteRoute($id: ID!) {
        deleteRoute(id: $id) {
            id
        }
    }
`;

export const PAUSE_ROUTE = gql`
    mutation PauseRoute($id: ID!) {
        pauseRoute(id: $id) {
            id
            status
            actionLog {
                id
                action
                timestamp
                userId
                userEmail
                stop {
                    id
                    name
                }
            }
        }
    }
`;

export const RESUME_ROUTE = gql`
    mutation ResumeRoute($id: ID!) {
        resumeRoute(id: $id) {
            id
            status
            actionLog {
                id
                action
                timestamp
                userId
                userEmail
                stop {
                    id
                    name
                }
            }
        }
    }
`;

export const ADD_ROUTE = gql`
    mutation AddRoute($input: RouteInstructionsListInput!) {
        addRoute(input: $input) {
            id
            __typename
            name
            track
            status
            stops {
                id
                name
                __typename
                stationId
                type
                status
                asynchronousChildren
            }
        }
    }
`;

export const ADD_ROUTE_TEMPLATE = gql`
    mutation AddRouteTemplate($input: RouteInstructionsListInput!) {
        addRouteTemplate(input: $input) {
            id
            __typename
            name
            track
            stops {
                id
                name
                __typename
                stationId
                type
                asynchronousChildren
            }
        }
    }
`;

export const UPDATE_ROUTE_TEMPLATE = gql`
    mutation UpdateRouteTemplate($id: ID!, $input: RouteInstructionsListInput!) {
        updateRouteTemplate(id: $id, input: $input) {
            id
            __typename
            name
            track
            stops {
                id
                name
                __typename
                stationId
                type
                asynchronousChildren
            }
        }
    }
`;

export const UPDATE_ROUTE_TEMPLATE_NAME = gql`
    mutation UpdateRouteTemplateName($id: ID!, $name: String!) {
        updateRouteTemplateName(id: $id, name: $name) {
            id
            __typename
            name
            stops {
                id
                name
                __typename
                stationId
                type
                asynchronousChildren
            }
        }
    }
`;

export const UPDATE_ROUTE_NAME = gql`
    mutation UpdateRouteName($id: ID!, $name: String!) {
        updateRouteName(id: $id, name: $name) {
            id
            __typename
            name
            stops {
                id
                name
                __typename
                stationId
                type
                status
                asynchronousChildren
            }
        }
    }
`;

export const DELETE_ROUTE_TEMPLATE = gql`
    mutation DeleteRouteTemplate($id: ID!) {
        deleteRouteTemplate(id: $id) {
            id
        }
    }
`;

export const ADD_STATION = gql`
    mutation AddStation($input: AddStationInput!) {
        addStation(input: $input) {
            id
            name
            description
            remotePortalId
            tenantId
        }
    }
`;

export const DELETE_STATION = gql`
    mutation DeleteStation($id: ID!) {
        deleteStation(id: $id) {
            id
            name
        }
    }
`;

export const UPDATE_ROUTE = gql`
    mutation UpdateRoute($id: ID!, $input: RouteInstructionsListInput!) {
        updateRoute(id: $id, input: $input) {
            id
            __typename
            name
            track
            status
            stops {
                id
                name
                __typename
                stationId
                type
                status
                asynchronousChildren
            }
        }
    }
`;

export const ENABLE_STOP_ASYNCHRONOUS_CHILDREN = gql`
    mutation EnableStopAsynchronousChildren($id: ID!) {
        enableStopAsynchronousChildren(id: $id) {
            id
            name
            type
            description
        }
    }
`;

export const DISABLE_STOP_ASYNCHRONOUS_CHILDREN = gql`
    mutation DisableStopAsynchronousChildren($id: ID!) {
        disableStopAsynchronousChildren(id: $id) {
            id
            name
            type
            description
        }
    }
`;

export const ADD_ADMIN_USER = /* GraphQL */ `
    mutation AddAdminUser($username: AWSEmail!) {
        addAdminUser(username: $username) {
            id
            tenantId
            email
            enabled
            group
            created
            modified
            status
        }
    }
`;

export const ADD_STANDARD_USER = /* GraphQL */ `
    mutation AddStandardUser($username: AWSEmail!) {
        addStandardUser(username: $username) {
            id
            tenantId
            email
            enabled
            group
            created
            modified
            status
        }
    }
`;

export const DELETE_STANDARD_USER = /* GraphQL */ `
    mutation DeleteStandardUser($id: String!) {
        deleteStandardUser(id: $id) {
            id
            tenantId
        }
    }
`;

export const DELETE_ADMIN_USER = /* GraphQL */ `
    mutation DeleteStandardUser($id: String!) {
        deleteAdminUser(id: $id) {
            id
            tenantId
        }
    }
`;

export const CHANGE_ADMIN_USER_TO_STANDARD = /* GraphQL */ `
    mutation ChangeAdminToStandardUser($id: String!) {
        changeAdminToStandardUser(id: $id) {
            id
            group
            tenantId
        }
    }
`;

export const CHANGE_STANDARD_USER_TO_ADMIN = /* GraphQL */ `
    mutation ChangeStandardUserToAdmin($id: String!) {
        changeStandardUserToAdmin(id: $id) {
            id
            group
            tenantId
        }
    }
`;

export const ACTIVATE_USER = /* GraphQL */ `
    mutation ActivteUser($id: String!) {
        activateUser(id: $id) {
            id
            enabled
            tenantId
        }
    }
`;

export const DEACTIVATE_USER = /* GraphQL */ `
    mutation DeactivateUser($id: String!) {
        deactivateUser(id: $id) {
            id
            enabled
            tenantId
        }
    }
`;

export const CHANGE_PLAN = /* GraphQL */ `
    mutation changePlan($planId: String!) {
        changePlan(planId: $planId) {
            id
            planId
            cancelPlanAt
            plan {
                id
                title
                price
                statementDescriptor
                featureList
                totalUsers
            }
            users {
                id
                enabled
            }
        }
    }
`;

export const CANCEL_PAID_PLAN_AT_PERIOD_END = /* GraphQL */ `
    mutation cancelPaidPlanAtPeriodEnd {
        cancelPaidPlanAtPeriodEnd {
            id
            cancelPlanAt
        }
    }
`;

export const REACTIVATE_CANCELING_PAID_PLAN = /* GraphQL */ `
    mutation reactivateCancelingPaidPlan {
        reactivateCancelingPaidPlan {
            id
            cancelPlanAt
        }
    }
`;

export const CREATE_PLAN_INTENT = /* GraphQL */ `
    mutation createPlanIntent($planId: String!) {
        createPlanIntent(planId: $planId) {
            planId
            clientSecret
        }
    }
`;

export const CREATE_PAYMENT_METHOD_INTENT = /* GraphQL */ `
    mutation createPaymentMethodIntent {
        createPaymentMethodIntent {
            clientSecret
        }
    }
`;

export const CONFIRM_ADD_PLAN = /* GraphQL */ `
    mutation confirmAddPlan($paymentMethodId: String!, $planId: String!, $setupIntentClientSecret: String!) {
        confirmAddPlan(paymentMethodId: $paymentMethodId, planId: $planId, setupIntentClientSecret: $setupIntentClientSecret) {
            id
            planId
            plan {
                id
                title
                price
                statementDescriptor
                featureList
                totalUsers
            }
        }
    }
`;

export const CONFIRM_ADD_PAYMENT_METHOD = /* GraphQL */ `
    mutation confirmAddPaymentMethod($paymentMethodId: String!, $setupIntentClientSecret: String!) {
        confirmAddPaymentMethod(paymentMethodId: $paymentMethodId, setupIntentClientSecret: $setupIntentClientSecret) {
            id
            paymentType
            expirationDate
            last4
            cardType
            default
        }
    }
`;

export const SET_DEFAULT_PAYMENT_METHOD = /* GraphQL */ `
    mutation setDefaultPaymentMethod($paymentMethodId: String!) {
        setDefaultPaymentMethod(paymentMethodId: $paymentMethodId) {
            default
            id
        }
    }
`;

export const contactUs = /* GraphQL */ `
    mutation contactUs($input: ContactUsInput!) {
        contactUs(input: $input) {
            messageType
            message
        }
    }
`;

export const DELETE_PAYMENT_METHOD = /* GraphQL */ `
    mutation deletePaymentMethod($paymentMethodId: String!) {
        deletePaymentMethod(paymentMethodId: $paymentMethodId) {
            id
        }
    }
`;

export const ENABLE_DELETE_ACCOUNT = /* GraphQL */ `
    mutation enableDeleteAccount {
        enableDeleteAccount {
            id
            deleteAccountFlag
        }
    }
`;

export const DISABLE_DELETE_ACCOUNT = /* GraphQL */ `
    mutation disableDeleteAccount {
        disableDeleteAccount {
            id
            deleteAccountFlag
        }
    }
`;

export const DELETE_ACCOUNT = /* GraphQL */ `
    mutation deleteAccount {
        deleteAccount {
            id
        }
    }
`;

export const CREATE_EXAMPLE_WORKFLOWS = gql`
    mutation CreateExampleWorkflows($input: CreateExampleWorkflowsInput!) {
        createExampleWorkflows(input: $input) {
            routes {
                id
                name
                status
                track
                stops {
                    id
                    name
                    stationId
                    type
                    status
                    asynchronousChildren
                }
            }
            stations {
                id
                name
                description
                tenantId
            }
        }
    }
`;
